import React, { useCallback, useEffect, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { voucherNumberService } from 'api/services';
import type { VoucherNumberPreviewDto } from 'api/types';

// ============================================================================
// DocumentNumberField (ADR-0030) — voucher number entry with a NON-consuming
// tentative preview. For AUTO voucher types it shows a read-only "assigned on
// Save" field plus a tentative-next hint (with last-issued + a refresh) — the
// number is clearly not final. For MANUAL (or AUTO + allowManualEntry) it shows
// an editable input. In edit mode it shows the already-allocated number read-only.
// Reusable across all transactional entry screens.
// ============================================================================

// Shown as a hover tooltip on the tentative hint's info icon (keeps the field
// compact instead of spending a second line on this caption).
const TENTATIVE_HINT = 'The final number is assigned on Save — this tentative number may change if another document is posted first.';

interface DocumentNumberFieldProps {
  voucherTypeId: string;
  /** Document date — affects the series reset key, so the preview re-peeks on change. */
  date: string;
  /** Edit mode: the already-allocated number (shown read-only; no preview). */
  existingNumber?: string | null;
  /** Controlled manual-entry value (used when the type is MANUAL / allows manual). */
  manualValue: string;
  onManualChange: (v: string) => void;
  label?: string;
  required?: boolean;
  error?: string;
  /** Compact layout (0.8125rem label/input) for tight header grids, e.g. Journal Entry. */
  compact?: boolean;
}

const DocumentNumberField: React.FC<DocumentNumberFieldProps> = ({
  voucherTypeId, date, existingNumber, manualValue, onManualChange, label = 'Document No.', required, error, compact,
}) => {
  const [preview, setPreview] = useState<VoucherNumberPreviewDto | null>(null);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  const fetchPreview = useCallback(async () => {
    if (!voucherTypeId || existingNumber) { setPreview(null); return; }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const p = await voucherNumberService.getPreview(voucherTypeId, date || undefined);
      if (reqId === reqIdRef.current) setPreview(p);
    } catch {
      if (reqId === reqIdRef.current) setPreview(null);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [voucherTypeId, date, existingNumber]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const isManual = preview?.numberingMethod === 'MANUAL';
  const allowsManual = isManual || preview?.allowManualEntry;

  // Compact layout for tight header grids (e.g. Journal Entry's form-grid-4).
  const rootClass = compact ? undefined : 'form-group';
  const labelClass = compact ? undefined : 'form-label';
  const labelStyle: React.CSSProperties | undefined = compact
    ? { fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block', color: 'var(--text-secondary)', fontWeight: 500 }
    : undefined;
  const compactInput: React.CSSProperties = compact ? { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' } : {};

  // Edit mode — locked, existing number.
  if (existingNumber) {
    return (
      <div className={rootClass} data-field="documentNumber">
        <label className={labelClass} style={labelStyle}>{label}</label>
        <input className="form-input" value={existingNumber} readOnly style={{ background: 'var(--bg-secondary)', ...compactInput }} />
      </div>
    );
  }

  return (
    <div className={rootClass} data-field="documentNumber">
      <label className={labelClass} style={labelStyle}>
        {label}{required && isManual && <span className="text-danger"> *</span>}
      </label>

      {!voucherTypeId ? (
        <input className="form-input" value="— select voucher type —" readOnly style={{ background: 'var(--bg-secondary)', ...compactInput }} />
      ) : allowsManual ? (
        <input
          className={`form-input${error ? ' form-field-error' : ''}`}
          value={manualValue}
          onChange={e => onManualChange(e.target.value)}
          placeholder={isManual ? 'Enter number' : `Auto: ${preview?.tentativeNextNumber || '—'} (override optional)`}
          style={compactInput}
        />
      ) : (
        <input
          className="form-input"
          value={loading ? 'Loading…' : 'Auto — assigned on Save'}
          readOnly
          style={{ background: 'var(--bg-secondary)', fontStyle: 'italic', color: 'var(--text-secondary)', ...compactInput }}
        />
      )}

      {/* Tentative hint (AUTO only) — clearly not final. */}
      {voucherTypeId && !isManual && preview?.tentativeNextNumber && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
          <span>
            Next <span style={{ fontStyle: 'italic' }}>(tentative)</span>:{' '}
            <strong style={{ color: 'var(--text-primary)' }}>{preview.tentativeNextNumber}</strong>
          </span>
          {preview.lastNumber && <span style={{ color: 'var(--text-muted)' }}>· last {preview.lastNumber}</span>}
          <button
            type="button"
            title="Refresh — re-check the next number"
            onClick={fetchPreview}
            disabled={loading}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 0.15rem', display: 'inline-flex', alignItems: 'center' }}
          >
            <IconSystem.refresh size={12} />
          </button>
          {/* Caption moved off a second line into a hover tooltip to keep the field compact. */}
          <span
            title={TENTATIVE_HINT}
            aria-label={TENTATIVE_HINT}
            style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help', color: 'var(--text-muted)' }}
          >
            <IconSystem.info size={12} />
          </span>
        </div>
      )}
      {error && <div className="form-error-message">{error}</div>}
    </div>
  );
};

export default DocumentNumberField;
